import { OnboardingV2DealSizeBucket } from "@redotech/cockroachdb-codegen/tables/onboarding-v2-deal-size-bucket";
import * as z from "zod";
import {
  returnsAnalyticsDataQualitySchema,
  returnsRevenueBundleSchema,
} from "./returns-analytics-shared";

/**
 * Input: current date range + optional previous date range for
 * period-over-period comparison. When previous is omitted, the
 * `previous` field in the output is null.
 */
export const getReturnsActivationFunnelInputSchema = z.object({
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
  previousStartDate: z.string().min(1).optional(),
  previousEndDate: z.string().min(1).optional(),
  /** Include weekly KPI trend for scorecard sparklines. Default: true. */
  includeTrend: z.boolean().default(true),
  /** Exclude self-serve rows with <60s recorded step time (matches Funnel). Default: true. */
  filterLowIntent: z.boolean().optional(),
});
export type GetReturnsActivationFunnelInput = z.infer<
  typeof getReturnsActivationFunnelInputSchema
>;

/** Exhaustive over the enum, so a new schema value must be given a position. */
const DEAL_SIZE_BUCKET_DISPLAY_ORDER: Record<
  OnboardingV2DealSizeBucket,
  number
> = {
  [OnboardingV2DealSizeBucket.Under1k]: 0,
  [OnboardingV2DealSizeBucket._1kTo5k]: 1,
  [OnboardingV2DealSizeBucket._5kTo25k]: 2,
  [OnboardingV2DealSizeBucket._25kTo100k]: 3,
  [OnboardingV2DealSizeBucket._100kPlus]: 4,
  [OnboardingV2DealSizeBucket.Unknown]: 5,
};
/** CRDB `onboarding_v2_deal_size_bucket` values, in display order. */
export const RETURNS_DEAL_SIZE_BUCKETS: readonly OnboardingV2DealSizeBucket[] =
  Object.values(OnboardingV2DealSizeBucket).sort(
    (a, b) =>
      DEAL_SIZE_BUCKET_DISPLAY_ORDER[a] - DEAL_SIZE_BUCKET_DISPLAY_ORDER[b],
  );

/**
 * Cohort = loaded self-serve rows in the window, classified by routing. "Went
 * live" = completed + `first_revenue_at` set, including assisted activation.
 */
const routedCohortSchema = z.object({
  /** Rows routed into self-serve (exact + inferred) — the rate denominator. */
  routedSelfServe: z.number().int().min(0),
  routedToCall: z.number().int().min(0),
  /** Completed rows among the routed-self-serve cohort. */
  completed: z.number().int().min(0),
  /** Completed rows with `first_revenue_at IS NOT NULL`. */
  wentLive: z.number().int().min(0),
  /** Routed Cohort Activation Rate: wentLive / routedSelfServe. */
  activationPct: z.number().min(0).max(100),
  /** Auto-Push Activation Rate: autoPushSplit.autoPushTrue / routedSelfServe. */
  autoPushActivationPct: z.number().min(0).max(100),
  /** `autoPushAtCompletion` split among wentLive rows; unknown = historical. */
  autoPushSplit: z.object({
    autoPushTrue: z.number().int().min(0),
    autoPushFalse: z.number().int().min(0),
    autoPushUnknown: z.number().int().min(0),
  }),
});
export type ReturnsRoutedCohortSummary = z.infer<typeof routedCohortSchema>;

/** Weekly `started_at` cohort (UTC weeks) of routed-self-serve rows. */
const weeklyRoutedCohortPointSchema = z.object({
  weekStart: z.date(),
  routedSelfServe: z.number().int().min(0),
  wentLive: z.number().int().min(0),
  activationPct: z.number().min(0).max(100),
  /** Cohort younger than 14 days — later completions can still change it. */
  immature: z.boolean(),
});
export type ReturnsWeeklyRoutedCohortPoint = z.infer<
  typeof weeklyRoutedCohortPointSchema
>;

/** Routed-self-serve cohort per deal-size bucket; exhaustive, zero-filled. */
const routedBucketRowSchema = z.object({
  bucket: z.enum(OnboardingV2DealSizeBucket),
  started: z.number().int().min(0),
  completed: z.number().int().min(0),
  wentLive: z.number().int().min(0),
  activationPct: z.number().min(0).max(100),
});
export type ReturnsRoutedBucketRow = z.infer<typeof routedBucketRowSchema>;

/**
 * Routed-to-call counts. `exact` comes from persisted routing decisions;
 * `inferred` is classified from historical signals and current Team volume.
 */
const routedToCallReasonsSchema = z.object({
  exact: z.object({
    volumeAtOrAboveThreshold: z.number().int().min(0),
    provisionalVolumeAtOrAboveThreshold: z.number().int().min(0),
    volumeUnavailableAfterPolling: z.number().int().min(0),
  }),
  inferred: z.object({
    exitModalHelp: z.number().int().min(0),
    /** Schedule-call visit + current volume >= threshold. */
    threshold: z.number().int().min(0),
    /** Schedule-call visit + current volume < threshold — likely null volume at routing time. */
    possibleNullVolume: z.number().int().min(0),
    /** Schedule-call visit with no current volume available. */
    dataMissing: z.number().int().min(0),
  }),
});
export type ReturnsRoutedToCallReasons = z.infer<
  typeof routedToCallReasonsSchema
>;

const snapshotSchema = z.object({
  hubspotDeals: z.object({
    total: z.number().int().min(0),
    closedWon: z.number().int().min(0),
    closedLost: z.number().int().min(0),
    inProgress: z.number().int().min(0),
    closedWonRevenue: returnsRevenueBundleSchema,
  }),
  selfServeContribution: z.object({
    /** Closed-won deals whose team has `onboarding_v2_progress.status = 'completed'`. */
    closedWonViaSelfServe: z.number().int().min(0),
    /** Closed-won deals whose team has `first_revenue_at IS NOT NULL`. */
    closedWonWentLive: z.number().int().min(0),
    /** Primary KPI: closedWonViaSelfServe / closedWon. */
    pctOfClosedWonViaSelfServe: z.number().min(0).max(100),
    /** Secondary: closedWonWentLive / closedWon. */
    pctOfClosedWonWentLive: z.number().min(0).max(100),
  }),
  selfServeFunnel: z.object({
    started: z.number().int().min(0),
    completed: z.number().int().min(0),
    linkedToHubSpot: z.number().int().min(0),
    wentLive: z.number().int().min(0),
    churned: z.number().int().min(0),
    reactivated: z.number().int().min(0),
  }),
  routedCohort: routedCohortSchema,
});
export type ReturnsActivationFunnelSnapshot = z.infer<typeof snapshotSchema>;

/** Per-week KPI point for scorecard sparklines. */
const trendPointSchema = z.object({
  weekStart: z.date(),
  closedWonCount: z.number().int().min(0),
  viaSelfServeCount: z.number().int().min(0),
  wentLiveCount: z.number().int().min(0),
  pctViaSelfServe: z.number().min(0).max(100),
});
export type ReturnsActivationTrendPoint = z.infer<typeof trendPointSchema>;

export const returnsActivationFunnelOutputSchema = snapshotSchema.extend({
  /** Previous-period snapshot (same shape). Null when no comparison requested. */
  previous: snapshotSchema.nullable(),
  /** Weekly KPI trend within the current window (for sparklines). */
  trend: z.array(trendPointSchema),
  /** Weekly routed cohorts (current window, ascending week). */
  weeklyCohorts: z.array(weeklyRoutedCohortPointSchema),
  /** Routed-self-serve cohort per deal-size bucket (current window). */
  byBucket: z.array(routedBucketRowSchema),
  /** Routed-to-call reason counts (current window). */
  routedToCallReasons: routedToCallReasonsSchema,
  dataQuality: returnsAnalyticsDataQualitySchema,
});
export type GetReturnsActivationFunnelOutput = z.infer<
  typeof returnsActivationFunnelOutputSchema
>;

export const getReturnsActivationFunnel = {
  name: "getReturnsActivationFunnel" as const,
  input: getReturnsActivationFunnelInputSchema,
  output: returnsActivationFunnelOutputSchema,
};
